import { useEffect, useRef } from 'react';
import { useUIStore, useSettingsStore } from '@/stores';
import type { ScreenType, AudioSettings } from '@/stores';

/**
 * メニュー/タイトル系画面で BGM (menu.mp3) を再生する非表示コンポーネント。
 *
 * - HTML5 Audio を useRef で1個だけ生成し、画面遷移で作り直さない（毎回0秒からの再生を防止）
 * - 非ゲーム画面 かつ musicEnabled かつ 音量>0 のときに再生、game_board では pause
 * - 音量は SoundManager と一致: (masterVolume/100) * (musicVolume/100)
 * - autoplay ブロック時は初回ユーザー操作(pointerdown/keydown)で再生開始
 * - 設定変更は subscribe でライブ反映
 */

const MENU_SCREENS: ReadonlySet<ScreenType> = new Set<ScreenType>([
  'title',
  'main_menu',
  'solo_setup',
  'local_setup',
  'online_setup',
]);

/** SoundManager と同じ計算式で 0..1 のミュージック音量を求める */
function computeMusicVolume(audio: AudioSettings): number {
  if (!audio.musicEnabled) {
    return 0;
  }
  const vol = (audio.masterVolume / 100) * (audio.musicVolume / 100);
  return Math.min(1, Math.max(0, vol));
}

function MenuMusic(): null {
  const activeScreen = useUIStore((state) => state.activeScreen);

  // Audio インスタンスは1度だけ生成する
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // autoplay 解除用リスナーの登録状態を追跡（多重登録防止）
  const unlockBoundRef = useRef(false);

  if (audioRef.current === null) {
    const el = new Audio('/assets/sounds/bgm/menu.mp3');
    el.loop = true;
    el.preload = 'auto';
    audioRef.current = el;
  }

  // 設定変更（音量/有効フラグ）をライブ反映
  useEffect(() => {
    const applyVolume = () => {
      const el = audioRef.current;
      if (!el) return;
      el.volume = computeMusicVolume(useSettingsStore.getState().audio);
    };
    applyVolume();
    const unsubscribe = useSettingsStore.subscribe(applyVolume);
    return () => {
      unsubscribe();
    };
  }, []);

  // 画面に応じた再生/停止制御
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const audio = useSettingsStore.getState().audio;
    const volume = computeMusicVolume(audio);
    const shouldPlay = MENU_SCREENS.has(activeScreen) && volume > 0;

    el.volume = volume;

    if (!shouldPlay) {
      el.pause();
      return;
    }

    // 再生中の Audio への play() は no-op。currentTime はリセットしない。
    const playPromise = el.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // autoplay がブロックされた場合、初回ユーザー操作で再生を試みる
        if (unlockBoundRef.current) return;
        unlockBoundRef.current = true;

        const unlock = () => {
          unlockBoundRef.current = false;
          // 解除時点でまだメニュー画面かつ有効な場合のみ再生
          const liveAudio = useSettingsStore.getState().audio;
          const liveVolume = computeMusicVolume(liveAudio);
          const live = audioRef.current;
          if (
            live &&
            liveVolume > 0 &&
            MENU_SCREENS.has(useUIStore.getState().activeScreen)
          ) {
            live.volume = liveVolume;
            live.play().catch(() => {
              /* それでも失敗した場合は静かに諦める */
            });
          }
        };

        window.addEventListener('pointerdown', unlock, { once: true });
        window.addEventListener('keydown', unlock, { once: true });
      });
    }
  }, [activeScreen]);

  // unmount 時に停止
  useEffect(() => {
    return () => {
      const el = audioRef.current;
      if (el) {
        el.pause();
      }
    };
  }, []);

  return null;
}

export default MenuMusic;
